import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ListIcon,
  Link as ChakraLink,
  Box,
  Badge,
  Text,
  useBreakpointValue,
  Collapse,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';

interface NavItemProps {
  item: NavbarItem;
  isActive: boolean;
  collapse: boolean;
  depth?: number;
}

export const NavItem = ({ item, isActive, collapse, depth = 0 }: NavItemProps) => {
  const { label, path, icon, count, type, children } = item;
  const location = useLocation();
  const isMobile = useBreakpointValue({ base: true, md: false });
  const isChildActive = children?.some((c) => c.path && location.pathname === c.path) ?? false;
  const [isOpen, setIsOpen] = useState(isChildActive);
  const isSub = depth > 0;

  if (type === 'group') {
    return (
      <Box>
        <Box
          display='flex'
          alignItems='center'
          my={6}
          justifyContent='center'
          flexDirection={isMobile && collapse ? 'row' : 'column'}
          position='relative'
        >
          <Box
            as='button'
            onClick={() => setIsOpen((prev) => !prev)}
            display='flex'
            alignItems='center'
            gap={3}
            fontWeight='medium'
            color={isChildActive ? 'gray.900' : 'gray.600'}
            w='full'
            justifyContent={collapse ? 'flex-start' : 'center'}
            borderRadius='md'
            p={2}
            bg={isChildActive ? 'primary.100' : 'transparent'}
            _hover={{ color: 'primary.700', transform: 'scale(1.05)', transition: 'transform 0.2s' }}
            transition='background-color 0.2s'
            textAlign='left'
          >
            {icon && (
              <ListIcon
                as={icon}
                fontSize={24}
                color={isChildActive ? 'gray.900' : 'gray.600'}
                marginX={collapse ? '' : 'auto'}
              />
            )}
            {collapse && (
              <>
                <Text fontSize='sm' fontWeight='medium' flex={1}>
                  {label}
                </Text>
                {isOpen ? (
                  <ChevronUpIcon fontSize={16} />
                ) : (
                  <ChevronDownIcon fontSize={16} />
                )}
              </>
            )}
          </Box>
        </Box>

        <Collapse in={isOpen && collapse} animateOpacity>
          <Box pl={3} borderLeft='2px solid' borderColor='primary.200' ml={6} mb={1}>
            {children?.map((child, idx) => (
              <NavItem
                key={idx}
                item={child}
                isActive={location.pathname === child.path}
                collapse={collapse}
                depth={depth + 1}
              />
            ))}
          </Box>
        </Collapse>
      </Box>
    );
  }

  return (
    <Box
      display='flex'
      alignItems='center'
      my={isSub ? 1 : 6}
      justifyContent='center'
      flexDirection={isMobile && collapse ? 'row' : 'column'}
      position='relative'
    >
      <ChakraLink
        as={Link}
        to={path}
        display='flex'
        alignItems='center'
        gap={2}
        _hover={{
          textDecoration: 'none',
          color: 'primary.700',
          transform: isSub ? 'none' : 'scale(1.05)',
          transition: 'color 0.2s',
        }}
        fontWeight='medium'
        color={isActive ? 'primary.600' : 'gray.500'}
        w='full'
        justifyContent={collapse ? 'flex-start' : 'center'}
        borderRadius='md'
        px={2}
        py={isSub ? 1 : 2}
        bg={isActive ? 'primary.50' : 'transparent'}
        transition='background-color 0.2s'
      >
        <ListIcon
          as={icon}
          fontSize={isSub ? 16 : 24}
          color={isActive ? 'primary.600' : 'gray.500'}
          marginX={collapse ? '' : 'auto'}
        />
        {collapse && (
          <Text fontSize={isSub ? 'xs' : 'sm'} fontWeight={isSub ? 'normal' : 'medium'}>
            {label}
          </Text>
        )}
      </ChakraLink>

      {collapse && count && (
        <Badge
          borderRadius='full'
          colorScheme='yellow'
          w={6}
          h={6}
          textAlign='center'
          fontSize='sm'
          display={collapse ? 'block' : 'none'}
          position='absolute'
          top={2}
          right={2}
          boxShadow='md'
        >
          {count}
        </Badge>
      )}
    </Box>
  );
};
